import * as Sentry from '@sentry/nuxt';

// Only run `init` when process.env.SENTRY_DSN is available.
if (process.env.SENTRY_DSN) {
  const { dsn, env } = useRuntimeConfig().public.sentry as unknown as {
    dsn?: string;
    env?: string;
  };

  Sentry.init({
    dsn,
    environment: env,
    integrations: [
      Sentry.browserTracingIntegration({
        router: useRouter(),
      }),
    ],
    // Full traces in dev, 10% in any built deployment. Keyed on `import.meta.dev`
    // rather than on SENTRY_ENV: that variable is free-form (it has carried
    // 'development' and 'local'), so a typo or an unset value would silently
    // restore 100% tracing on a production build — the expensive direction to
    // fail in. This signal is resolved at build time and cannot drift.
    tracesSampleRate: import.meta.dev ? 1.0 : 0.1,
    // Read by the replay integration when it attaches below, so the effective
    // sampling is unchanged by the lazy load.
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });

  // Session Replay is attached after init instead of being listed in
  // `integrations`. Importing it eagerly pulled the whole replay recorder into
  // the initial client bundle for every visitor, even though only 10% of
  // sessions are ever sampled. lazyLoadIntegration fetches the recorder on
  // demand instead.
  //
  // This trades bundle weight for a runtime script fetch from
  // browser.sentry-cdn.com on sampled sessions, and that trade was made
  // deliberately: the fetch happens after load on 10% of sessions and blocks
  // nothing, whereas the eager import cost every visitor 164KB up front. A
  // first-party alternative (dynamic `import()` of the replay module) is not
  // used because this file already imports `@sentry/nuxt` statically, so the
  // bundler would hoist the shared module back into the initial chunk and
  // undo the saving. There is no CSP `script-src` on this origin to block the
  // CDN fetch — if one is ever added, it must allowlist browser.sentry-cdn.com
  // or this silently stops recording replays.
  Sentry.lazyLoadIntegration('replayIntegration')
    .then((replayIntegration) => {
      Sentry.addIntegration(replayIntegration());
    })
    .catch(() => {
      // Replay is best-effort telemetry; failing to load it must never break
      // the page or suppress ordinary error capture.
    });
}
